import { defineStore } from "pinia";
import { computed, ref } from "vue";
import axios from "axios";
import useHelpers from "composables/useHelpers.js";
import { useRoute, useRouter } from "vue-router";
import { useQuasar } from "quasar";

const DEFAULTNOTIFICATIONSPERPAGE = 10;

/**
 * Notification Store
 *
 * This store manages the state and actions related to notifications.
 */
export const useNotificationStore = defineStore("notifications", () => {
  // Route
  const route = useRoute();
  const router = useRouter();
  const $q = useQuasar();

  // Refs
  const notifications = ref([]);
  const unreadNotificationsCount = ref(0);
  const unfinishedRequiredTasks = ref(0);
  const isFetching = ref({
    notifications: false,
  });

  // Helpers
  const { setPaginationMeta } = useHelpers();
  /**
   * Gets the title of a notification based on its message.
   * @param {string} message - The notification message.
   * @returns {string} The title of the notification.
   */
  const getNotificationTitle = (message) => {
    const condition = (searchTerm) => {
      return message?.toLowerCase().includes(searchTerm) ?? false;
    };

    if (condition("actieplan")) return "Actieplan";
    if (condition("reactie geplaatst bij cluster")) return "Leerdoel";
    if (condition("portfolio")) return "Portfolio";
    if (condition("feedback")) return "Feedback";
    if (condition("taak")) return "Taak";
    if (condition("leerling-stoplicht")) return "Leerling stoplicht";
    if (condition("activiteit")) return "Portfolio";
  };

  // Methods
  /**
   * Fetches notifications for a user.
   * @param {Object} options - The options for fetching notifications.
   * @param {number} [options.id=null] - The user ID.
   * @param {boolean} [options.specificUser=false] - Whether to fetch notifications for a specific user.
   * @param {Object} [options.pagination=null] - The pagination model.
   * @returns {Promise<Array<Object>>} The fetched notifications.
   */
  const fetchNotifications = async ({ pagination = null } = {}) => {
    const { page, per_page } = pagination;

    isFetching.value["notifications"] = true;

    try {
      const {
        data: { data: fetchedData, meta },
      } = await axios.get(`/api/user/notifications`, {
        params: {
          ...{ page, per_page },
        },
      });

      Object.assign(pagination, setPaginationMeta(meta));

      notifications.value = fetchedData;

      return fetchedData;
    } catch (error) {
      console.log("🚀 ~ fetchNotifications ~ error", error);
      if (axios.isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["notifications"] = false;
    }
  };

  const fetchActivityNotifications = async ({
    pagination = {},
    studentId = null,
  } = {}) => {
    if (studentId === null) throw new Error("Student ID is required");

    const { page, per_page } = pagination;

    isFetching.value["notifications"] = true;

    try {
      const {
        data: { data: fetchedData, meta },
      } = await axios.get(`/api/student/${studentId}/notification-activities`, {
        params: {
          ...{ page, per_page },
        },
      });

      Object.assign(pagination, setPaginationMeta(meta));

      notifications.value = fetchedData;

      return fetchedData;
    } catch (error) {
      console.log("🚀 ~ fetchNotifications ~ error", error);
      if (axios.isAxiosError(error)) {
        console.log(error);

        return Promise.reject(error.response);
      }
    } finally {
      isFetching.value["notifications"] = false;
    }
  };

  /**
   * Fetches the count of unread notifications.
   * @returns {Promise<number>} The count of unread notifications.
   */
  const fetchUnseenNotificationsCount = async () => {
    try {
      const { data } = await axios.get(`/api/user/unseen-notification-count`);

      unreadNotificationsCount.value = data.count;

      return unreadNotificationsCount.value;
    } catch (error) {
      console.log("🚀 ~ notificationStore.js ~ error:", error);
    }
  };

  /**
   * Fetches the count of unfinished required tasks.
   * @returns {Promise<number>} The count of unfinished required tasks.
   */
  const fetchUnfinishedRequiredTasksCount = async () => {
    try {
      const { data } = await axios.get(
        `/api/student/open-required-tasks-count`,
      );

      unfinishedRequiredTasks.value = data.count;

      return unfinishedRequiredTasks.value;
    } catch (error) {
      console.log("🚀 ~ notificationStore.js ~ error:", error);
    }
  };

  /**
   * Sets a notification as read.
   * @returns {Promise<Object>} The response data.
   * @param {array} notifications
   */
  const setNotificationsAsRead = async (notifications = []) => {
    try {
      const { data } = await axios.post(`/api/user/set-notifications-read`, {
        notifications,
        set_as_read: true,
      });

      return data;
    } catch (error) {
      console.log("🚀 ~ notificationStore.js ~ error:", error);
    }
  };

  /**
   * Sets all notifications as read.
   * @returns {Promise<Object>} The response data.
   */
  const setAllNotificationsAsRead = async () => {
    try {
      const { data } = await axios.post(`/api/user/set-notifications-read/all`);

      return data;
    } catch (error) {
      console.log("🚀 ~ notificationStore.js ~ error:", error);
    }
  };

  let activeRequests = 0;
  axios.interceptors.request.use((config) => {
    // log the specific request
    activeRequests++;
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      activeRequests--;
      return response;
    },
    (error) => {
      activeRequests--;
      return Promise.reject(error);
    },
  );

  function waitForNetworkIdle(idleDelay = 50, checkInterval = 50) {
    return new Promise((resolve) => {
      let lastActiveTime = Date.now();

      const interval = setInterval(() => {
        if (activeRequests === 0) {
          if (Date.now() - lastActiveTime >= idleDelay) {
            clearInterval(interval);
            resolve();
          }
        } else {
          lastActiveTime = Date.now();
        }
      }, checkInterval);
    });
  }

  // Route mapping
  const allRoutes = computed(() => [
    { label: "my-ratings", name: "myRatingsId" },
    { label: "my-meetings-meetings", name: "MyMeetingsMeetingId" },
    { label: "my-students-meetings", name: "myStudentsStudentMeetings" },
    {
      label: "my-lessongroups-meetings",
      name: "myLessongroupsStudentMeetings",
    },
    { label: "my-meetings-group-meetings", name: "MyMeetingsMeetingId" },
    { label: "my-students-group-meetings", name: "myStudentsGroupMeetings" },
    {
      label: "my-lessongroups-group-meetings",
      name: "myLessongroupsGroupMeetings",
    },
    { label: "my-meetings-transition-meetings", name: "MyMeetingsMeetingId" },
    { label: "my-students-portfolio", name: "myStudentsPortfolio" },
    {
      label: "my-students-learning-objectives",
      name: "myStudentsLearningObjectives",
    },
    {
      label: "my-learning-objectives",
      name: "myLearningObjectives",
    },
    {
      label: "my-lessongroups-learning-objectives",
      name: "myLessongroupsLearningObjectives",
    },
    { label: "my-portfolio", name: "portfolio" },
    { label: "task-slug", name: "task" },
  ]);

  const openStudentSidebar = async (data) => {
    // const { studentId } = data;
    //
    // if (!studentId) throw new Error("Student ID is required");
    //
    // await router.push({
    //   query: {
    //     studentInfo: studentId,
    //   },
    // });
  };

  const openPortfolioTab = async (data) => {
    const { studentId } = data;

    console.log("voor push");

    return router.push({
      query: {
        studentInfo: studentId,
        view: "portfolio",
      },
    });
  };

  const highlightItem = (data, routeQuery, action, actions) => {
    let selectors = [];

    const requireFields = (fields, source) => {
      fields.forEach((field) => {
        if (!source[field]) throw new Error(`${field} is missing from dataset`);
      });
    };

    switch (action) {
      case "highlight-meeting": {
        requireFields(["meetingId"], data);
        selectors.push(`[data-meeting-id="${data.meetingId}"]`);
        break;
      }
      case "highlight-student-report": {
        requireFields(["studentId", "meetingId"], data);
        selectors.push(
          `[data-meeting-student="${data.meetingId}-${data.studentId}"]`,
        );
        break;
      }
      case "highlight-global-actionplan": {
        requireFields(["studentId", "id", "meetingId"], data);
        selectors.push(
          `[data-meeting-student="${data.meetingId}-${data.studentId}"]`,
        );
        selectors.push(`[data-global-actionplan-id="${data.id}"]`);
        break;
      }
      case "highlight-activity": {
        requireFields(["activityId"], data);
        if (actions.includes("open-portfolio-tab")) {
          selectors.push(
            `.sidebar__content-container [data-activity-id="${data.activityId}"]`,
          );
        } else {
          selectors.push(`[data-activity-id="${data.activityId}"]`);
        }

        break;
      }
      case "highlight-actionplan": {
        requireFields(["actionplanId", "studentId", "meetingId"], data);
        selectors.push(
          `[data-meeting-student="${data.meetingId}-${data.studentId}"]`,
        );
        selectors.push(`[data-actionplan-id="${data.actionplanId}"]`);
        break;
      }
      default:
        console.warn(`No mapped function for action: ${action}`);
    }

    (function scrollRecursive(index) {
      if (index >= selectors.length) return;

      const selector = selectors[index];
      let el = document.querySelector(selector);

      if (!el) {
        setTimeout(() => {
          el = document.querySelector(selector);
          if (!el) {
            $q.notify({
              type: "warning",
              message:
                "Actie is niet uitgevoerd. Heb je op een oude notificatie geklikt?",
            });
            throw new Error(
              `Target ${selector} for action ${action} not found`,
            );
          }
          scrollAndHighlight(el, index);
        }, 300);
      } else {
        scrollAndHighlight(el, index);
      }

      function scrollAndHighlight(element, index) {
        if (index === selectors.length - 1) {
          element.classList.add("highlighted");
          element.scrollIntoView({ behavior: "smooth", block: "start" });

          setTimeout(() => {
            element.classList.remove("highlighted");
          }, 3000);
        } else {
          element.scrollIntoView({ behavior: "smooth", block: "start" });

          setTimeout(() => {
            scrollRecursive(index + 1);
          }, 600);
        }
      }
    })(0);
  };

  const mappedActions = () => ({
    "highlight-meeting": highlightItem,
    "highlight-student-report": highlightItem,
    "highlight-global-actionplan": highlightItem,
    "open-student-sidebar": openStudentSidebar,
    "open-portfolio-tab": openPortfolioTab,
    "highlight-activity": highlightItem,
    "highlight-actionplan": highlightItem,
  });

  let isExecuting = false;

  const executeNotificationActions = async () => {
    if (isExecuting) return;
    isExecuting = true;

    try {
      const data = JSON.parse(route.query.data || "{}");
      const actionsQuery = route.query.actions;
      if (!actionsQuery) return;

      const routeQuery = route.query.route;
      const actions = JSON.parse(actionsQuery);
      const map = mappedActions();

      // Navigate to target route
      await router.replace({
        name: route.name,
        params: route.params,
        query: {
          ...route.query,
          actions: undefined,
          data: undefined,
          route: undefined,
        },
      });

      for (const action of actions) {
        const fn = map[action];
        if (typeof fn === "function") {
          await waitForNetworkIdle();
          await fn(data, routeQuery, action, actions);
        } else {
          console.warn(`No mapped function for action: ${action}`);
        }
      }
    } catch (error) {
      console.error("Failed to parse actions from query:", error);
    } finally {
      isExecuting = false;
    }
  };

  return {
    notifications,
    isFetching,
    fetchNotifications,
    fetchUnseenNotificationsCount,
    fetchUnfinishedRequiredTasksCount,
    getNotificationTitle,
    setNotificationsAsRead,
    setAllNotificationsAsRead,
    unreadNotificationsCount,
    unfinishedRequiredTasks,
    DEFAULTNOTIFICATIONSPERPAGE,
    executeNotificationActions,
    fetchActivityNotifications,
    allRoutes,
  };
});
